import type { ExecutionSeriesMarker } from '../chart/normalizeExecutionsToMarkers';
import { normalizeTimestampToMs } from '../time/normalizeTimestampToMs';
import { computeDeterministicPositionState } from './deterministicPrimitives';
import { resolveExecutionRows } from './resolveExecutionRows';

export type ReplayBoundaryMode = 'bar-open' | 'bar-close';

export const DEFAULT_REPLAY_BOUNDARY_MODE: ReplayBoundaryMode = 'bar-close';

export type ReplayBarInput = {
  time?: number | string;
  open?: number | string;
  high?: number | string;
  low?: number | string;
  close?: number | string;
};

export type ReplayExecutionInput = {
  id?: string | number;
  storageId?: string | number;
  executionId?: string | number;
  orderId?: string | number;
  timestamp?: number | string | Date;
  openTimestampMs?: number | string | Date;
  time?: number | string | Date;
  executedAt?: number | string | Date;
  date?: number | string | Date;
  t?: number | string | Date;
  side?: string;
  execSide?: string;
  action?: string;
  execSideCode?: string;
  type?: string;
  direction?: string;
  position?: string;
  volume?: number | string;
  qty?: number | string;
  quantity?: number | string;
  shares?: number | string;
  price?: number | string;
  avgPrice?: number | string;
  fillPrice?: number | string;
  entryPrice?: number | string;
  exitPrice?: number | string;
  entry_price?: number | string;
  avg_price?: number | string;
  commission?: number | string;
  fees?: number | string;
  fee?: number | string;
  commissionAndFees?: number | string;
  commissionsAndFees?: number | string;
  rowRealizedGross?: number | string;
  rowRealizedNet?: number | string;
  rowFees?: number | string;
};

export type NormalizedReplayExecution = {
  sourceId: string;
  originalIndex: number;
  timestampMs: number;
  activationBarIndex: number;
  candleIndex: number;
  side: 'buy' | 'sell';
  signedQty: number;
  qty: number;
  price: number;
  executionCostsPaid: number;
  realizedGross: number;
  realizedNet: number;
  realizedFees: number;
};

export type ReplayTradeLot = {
  side: 'long' | 'short';
  qty: number;
  price: number;
  feePerUnit: number;
  sourceId: string;
};

export type ReplayTradeState = {
  appliedExecutionIds: string[];
  appliedExecutions: NormalizedReplayExecution[];
  executedCount: number;
  netQuantity: number;
  currentPositionQty: number;
  currentSide: 'long' | 'short' | 'flat';
  side: 'long' | 'short' | 'flat';
  avgEntryPrice: number | null;
  breakEvenPrice: number | null;
  realizedGross: number;
  realizedPnl: number;
  unrealizedGross: number;
  unrealizedPnl: number;
  totalPnl: number;
  totalFeesPaid: number;
  realizedFeesPaid: number;
  openFeesPaid: number;
  lastAppliedExecutionId: string | null;
  openLots: ReplayTradeLot[];
};

export type ReplayStepSnapshot = ReplayTradeState & {
  currentBarIndex: number;
  replayVisibleBars: number;
  stepBarTimeMs: number | null;
  stepStartMs: number | null;
  stepEndMs: number | null;
  replayBoundaryTimeMs: number | null;
  boundaryMode: ReplayBoundaryMode;
  started: boolean;
  closed: boolean;
  statusLabel: 'WAITING' | 'LIVE' | 'CLOSED';
  detailLabel: string;
};

type NormalizedReplayBar = {
  index: number;
  timeMs: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
};

const DEFAULT_STEP_MS = 60_000;

function toFiniteNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function normalizeBarTimeToMs(value: unknown): number | null {
  return normalizeTimestampToMs(value);
}

export function normalizeExecutionTimeToMs(value: unknown): number | null {
  return normalizeTimestampToMs(value);
}

export function normalizeMarkerTimeToMs(value: unknown): number | null {
  return normalizeTimestampToMs(value);
}

function normalizeBars(bars: ReplayBarInput[]): NormalizedReplayBar[] {
  return bars.map((bar, index) => ({
    index,
    timeMs: normalizeBarTimeToMs(bar?.time),
    open: toFiniteNumber(bar?.open),
    high: toFiniteNumber(bar?.high),
    low: toFiniteNumber(bar?.low),
    close: toFiniteNumber(bar?.close),
  }));
}

function getValidBars(bars: ReplayBarInput[]): NormalizedReplayBar[] {
  return normalizeBars(bars).filter((bar) => bar.timeMs != null);
}

function inferStepMs(bars: NormalizedReplayBar[]): number {
  let minDiff = Number.POSITIVE_INFINITY;

  for (let i = 1; i < bars.length; i += 1) {
    const prev = bars[i - 1]?.timeMs;
    const curr = bars[i]?.timeMs;
    if (!Number.isFinite(prev) || !Number.isFinite(curr)) continue;
    const diff = Math.abs(Number(curr) - Number(prev));
    if (Number.isFinite(diff) && diff > 0) {
      minDiff = Math.min(minDiff, diff);
    }
  }

  return Number.isFinite(minDiff) && minDiff > 0 ? minDiff : DEFAULT_STEP_MS;
}

function parseExecutionSide(input: ReplayExecutionInput): 'buy' | 'sell' | null {
  const raw = String(
    input?.execSide
    ?? input?.side
    ?? input?.action
    ?? input?.type
    ?? input?.direction
    ?? input?.position
    ?? ''
  ).trim().toLowerCase();

  if (raw === '-' || raw === '--') return 'sell';
  if (raw === '+' || raw === '++') return 'buy';
  if (raw === 's' || raw.includes('sell') || raw.includes('short')) return 'sell';
  if (raw === 'b' || raw.includes('buy') || raw.includes('long')) return 'buy';

  const qty = Number(input?.qty ?? input?.quantity ?? input?.shares ?? input?.volume);
  if (Number.isFinite(qty) && qty !== 0) return qty < 0 ? 'sell' : 'buy';

  return null;
}

function parseExecutionQty(input: ReplayExecutionInput): number {
  const qty = Number(input?.qty ?? input?.quantity ?? input?.shares ?? input?.volume);
  if (!Number.isFinite(qty) || qty === 0) return 0;
  return Math.abs(qty);
}

function parseExecutionPrice(input: ReplayExecutionInput): number | null {
  return toFiniteNumber(
    input?.price
    ?? input?.avgPrice
    ?? input?.fillPrice
    ?? input?.entryPrice
    ?? input?.exitPrice
    ?? input?.entry_price
    ?? input?.avg_price
  );
}

function parseExecutionCosts(input: ReplayExecutionInput): number {
  const explicit = toFiniteNumber(input?.commissionAndFees ?? input?.commissionsAndFees);
  if (explicit != null) return explicit;
  return (toFiniteNumber(input?.commission) ?? 0) + (toFiniteNumber(input?.fees ?? input?.fee) ?? 0);
}

function getExecutionSourceId(execution: ReplayExecutionInput, originalIndex: number, timestampMs: number): string {
  return String(
    execution?.id
    ?? execution?.storageId
    ?? execution?.executionId
    ?? execution?.orderId
    ?? `${originalIndex}:${timestampMs}`
  );
}

function getBarAtOrAfterIndex(validBars: NormalizedReplayBar[], rawIndex: number): NormalizedReplayBar {
  return validBars.find((bar) => bar.index >= rawIndex) ?? validBars[validBars.length - 1];
}

function getNextBarStartMs(validBars: NormalizedReplayBar[], barIndex: number, stepMs: number): number | null {
  const next = validBars[barIndex + 1]?.timeMs;
  if (next != null) return Number(next);
  const current = validBars[barIndex]?.timeMs;
  return current != null ? (Number(current) + stepMs) : null;
}

function findExecutionReplayStepFromMs(
  executionTimeMs: number | null,
  bars: ReplayBarInput[],
  boundaryMode: ReplayBoundaryMode,
): number {
  const validBars = getValidBars(bars);
  if (!validBars.length) return 0;
  if (executionTimeMs == null) return validBars[0].index;

  for (const bar of validBars) {
    const boundaryTimeMs = getReplayBoundaryTimeMs(bar.index, bars, boundaryMode);
    if (boundaryTimeMs != null && executionTimeMs <= boundaryTimeMs) {
      return bar.index;
    }
  }

  return validBars[validBars.length - 1].index;
}

export function getReplayStepBoundaryMs(
  currentBarIndex: number,
  bars: ReplayBarInput[],
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): { stepStartMs: number | null; stepEndMs: number | null; replayBoundaryTimeMs: number | null; boundaryMode: ReplayBoundaryMode } {
  const validBars = getValidBars(bars);
  if (!validBars.length) {
    return {
      stepStartMs: null,
      stepEndMs: null,
      replayBoundaryTimeMs: null,
      boundaryMode,
    };
  }

  const clampedRawIndex = Math.max(0, Math.min(bars.length - 1, Math.floor(currentBarIndex)));
  const chosenBar = getBarAtOrAfterIndex(validBars, clampedRawIndex);
  const validIndex = validBars.findIndex((bar) => bar.index === chosenBar.index);
  const stepMs = inferStepMs(validBars);
  const stepStartMs = chosenBar.timeMs != null ? Number(chosenBar.timeMs) : null;
  const nextStartMs = validIndex >= 0 ? getNextBarStartMs(validBars, validIndex, stepMs) : null;
  const stepEndMs = stepStartMs != null && nextStartMs != null ? (nextStartMs - 1) : stepStartMs;
  const replayBoundaryTimeMs = boundaryMode === 'bar-open' ? stepStartMs : stepEndMs;

  return {
    stepStartMs,
    stepEndMs,
    replayBoundaryTimeMs,
    boundaryMode,
  };
}

export function getReplayBoundaryTimeMs(
  currentBarIndex: number,
  bars: ReplayBarInput[],
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): number | null {
  return getReplayStepBoundaryMs(currentBarIndex, bars, boundaryMode).replayBoundaryTimeMs;
}

export function findExecutionReplayStep(
  executionTimeInput: unknown,
  bars: ReplayBarInput[],
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): number {
  return findExecutionReplayStepFromMs(normalizeExecutionTimeToMs(executionTimeInput), bars, boundaryMode);
}

export function findContainingBarIndex(
  timestampInput: unknown,
  bars: ReplayBarInput[],
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): number {
  return findExecutionReplayStep(timestampInput, bars, boundaryMode);
}

export function getReplayStartIndex(
  firstExecutionTimeInput: unknown,
  bars: ReplayBarInput[],
  preRollCandles = 5,
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): number {
  if (!Array.isArray(bars) || bars.length === 0) return 0;
  const firstExecutionTimeMs = normalizeExecutionTimeToMs(firstExecutionTimeInput);
  if (firstExecutionTimeMs == null) return 0;
  const firstIndex = findExecutionReplayStepFromMs(firstExecutionTimeMs, bars, boundaryMode);
  return Math.max(0, firstIndex - Math.max(0, Math.floor(preRollCandles)));
}

export function normalizeReplayExecutions(
  executions: ReplayExecutionInput[],
  bars: ReplayBarInput[],
  options?: { multiplier?: number; boundaryMode?: ReplayBoundaryMode }
): NormalizedReplayExecution[] {
  if (!Array.isArray(executions) || executions.length === 0) return [];

  const boundaryMode = options?.boundaryMode ?? DEFAULT_REPLAY_BOUNDARY_MODE;
  const sortedInputs = executions
    .map((execution, originalIndex) => ({ execution, originalIndex }))
    .map(({ execution, originalIndex }) => ({
      execution,
      originalIndex,
      timestampMs: normalizeExecutionTimeToMs(
        execution?.openTimestampMs
        ?? execution?.timestamp
        ?? execution?.time
        ?? execution?.executedAt
        ?? execution?.date
        ?? execution?.t
      ),
    }))
    .filter((item) => item.timestampMs != null)
    .sort((a, b) => {
      const timeDiff = Number(a.timestampMs) - Number(b.timestampMs);
      return timeDiff !== 0 ? timeDiff : (a.originalIndex - b.originalIndex);
    });

  const multiplier = Number.isFinite(Number(options?.multiplier)) ? Number(options?.multiplier) : 1;
  const hasAuthoritativeFields = sortedInputs.every(({ execution }) => (
    Number.isFinite(Number(execution?.rowRealizedGross))
    && Number.isFinite(Number(execution?.rowRealizedNet))
    && Number.isFinite(Number(execution?.rowFees))
  ));

  const fallbackResolvedRows = hasAuthoritativeFields
    ? null
    : resolveExecutionRows(sortedInputs.map((item) => item.execution), { multiplier });

  return sortedInputs
    .map(({ execution, originalIndex, timestampMs }, sortedIndex) => {
      const side = parseExecutionSide(execution);
      const qty = parseExecutionQty(execution);
      const price = parseExecutionPrice(execution);
      if (timestampMs == null || !side || qty <= 0 || price == null) return null;

      const fallbackRow = fallbackResolvedRows?.[sortedIndex];
      const sourceId = getExecutionSourceId(execution, originalIndex, Number(timestampMs));
      const activationBarIndex = findExecutionReplayStepFromMs(Number(timestampMs), bars, boundaryMode);

      return {
        sourceId,
        originalIndex,
        timestampMs: Number(timestampMs),
        activationBarIndex,
        candleIndex: activationBarIndex,
        side,
        signedQty: side === 'buy' ? qty : -qty,
        qty,
        price,
        executionCostsPaid: parseExecutionCosts(execution),
        realizedGross: Number.isFinite(Number(execution?.rowRealizedGross))
          ? Number(execution?.rowRealizedGross)
          : (Number.isFinite(Number(fallbackRow?.rowRealizedGross)) ? Number(fallbackRow?.rowRealizedGross) : 0),
        realizedNet: Number.isFinite(Number(execution?.rowRealizedNet))
          ? Number(execution?.rowRealizedNet)
          : (Number.isFinite(Number(fallbackRow?.rowRealizedNet)) ? Number(fallbackRow?.rowRealizedNet) : 0),
        realizedFees: Number.isFinite(Number(execution?.rowFees))
          ? Number(execution?.rowFees)
          : (Number.isFinite(Number(fallbackRow?.rowFees)) ? Number(fallbackRow?.rowFees) : 0),
      } satisfies NormalizedReplayExecution;
    })
    .filter((execution): execution is NormalizedReplayExecution => execution != null);
}

export function getReachedExecutions(
  executions: NormalizedReplayExecution[],
  replayBoundaryTimeMs: number | null,
): NormalizedReplayExecution[] {
  if (replayBoundaryTimeMs == null) return [];

  return executions
    .filter((execution) => execution.timestampMs <= replayBoundaryTimeMs)
    .sort((a, b) => {
      const timeDiff = a.timestampMs - b.timestampMs;
      return timeDiff !== 0 ? timeDiff : (a.originalIndex - b.originalIndex);
    });
}

export function getExecutionsUpToReplayStep(
  currentBarIndex: number,
  bars: ReplayBarInput[],
  executions: NormalizedReplayExecution[],
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): NormalizedReplayExecution[] {
  return getReachedExecutions(executions, getReplayBoundaryTimeMs(currentBarIndex, bars, boundaryMode));
}

export function getReplayMarkPrice(bar: ReplayBarInput | null | undefined): number | null {
  const close = toFiniteNumber(bar?.close);
  if (close != null) return close;
  return toFiniteNumber(bar?.open);
}

export function buildReplayTradeState(
  reachedExecutions: NormalizedReplayExecution[],
  currentMarkPrice: number | null,
  options?: { multiplier?: number }
): ReplayTradeState {
  const multiplier = Number.isFinite(Number(options?.multiplier)) ? Number(options?.multiplier) : 1;
  const sortedExecutions = [...reachedExecutions].sort((a, b) => {
    const timeDiff = a.timestampMs - b.timestampMs;
    return timeDiff !== 0 ? timeDiff : (a.originalIndex - b.originalIndex);
  });

  const state = computeDeterministicPositionState({
    executions: sortedExecutions.map((execution) => ({
      id: execution.sourceId,
      timestamp: execution.timestampMs,
      side: execution.side,
      qty: execution.qty,
      price: execution.price,
      fee: execution.executionCostsPaid,
      originalIndex: execution.originalIndex,
    })),
    multiplier,
    markPrice: Number.isFinite(Number(currentMarkPrice)) ? Number(currentMarkPrice) : null,
  });

  const openLongLots: ReplayTradeLot[] = state.openLongLots.map((lot) => ({
    side: 'long',
    qty: lot.qty,
    price: lot.price,
    feePerUnit: lot.feePerUnit,
    sourceId: lot.phaseId,
  }));
  const openShortLots: ReplayTradeLot[] = state.openShortLots.map((lot) => ({
    side: 'short',
    qty: lot.qty,
    price: lot.price,
    feePerUnit: lot.feePerUnit,
    sourceId: lot.phaseId,
  }));

  return {
    appliedExecutionIds: sortedExecutions.map((execution) => execution.sourceId),
    appliedExecutions: sortedExecutions,
    executedCount: sortedExecutions.length,
    netQuantity: state.netQuantity,
    currentPositionQty: state.netQuantity,
    currentSide: state.side,
    side: state.side,
    avgEntryPrice: state.avgEntryPrice,
    breakEvenPrice: state.breakEvenPrice,
    realizedGross: state.realizedGross,
    realizedPnl: state.realizedPnl,
    unrealizedGross: state.unrealizedGross,
    unrealizedPnl: state.unrealizedPnl,
    totalPnl: state.totalPnl,
    totalFeesPaid: state.totalFeesPaid,
    realizedFeesPaid: state.realizedFeesPaid,
    openFeesPaid: state.openFeesPaid,
    lastAppliedExecutionId: sortedExecutions[sortedExecutions.length - 1]?.sourceId ?? null,
    openLots: [...openLongLots, ...openShortLots],
  };
}

function buildEmptyTradeState(): ReplayTradeState {
  return {
    appliedExecutionIds: [],
    appliedExecutions: [],
    executedCount: 0,
    netQuantity: 0,
    currentPositionQty: 0,
    currentSide: 'flat',
    side: 'flat',
    avgEntryPrice: null,
    breakEvenPrice: null,
    realizedGross: 0,
    realizedPnl: 0,
    unrealizedGross: 0,
    unrealizedPnl: 0,
    totalPnl: 0,
    totalFeesPaid: 0,
    realizedFeesPaid: 0,
    openFeesPaid: 0,
    lastAppliedExecutionId: null,
    openLots: [],
  };
}

export function filterExecutionMarkersForReplay(
  markers: ExecutionSeriesMarker[],
  currentBarIndex: number,
  bars: ReplayBarInput[],
  executions?: NormalizedReplayExecution[],
  boundaryMode: ReplayBoundaryMode = DEFAULT_REPLAY_BOUNDARY_MODE,
): ExecutionSeriesMarker[] {
  const replayBoundaryTimeMs = getReplayBoundaryTimeMs(currentBarIndex, bars, boundaryMode);
  if (replayBoundaryTimeMs == null) return [];

  const reachedExecutions = Array.isArray(executions)
    ? getReachedExecutions(executions, replayBoundaryTimeMs)
    : [];
  const reachedIds = new Set(reachedExecutions.map((execution) => execution.sourceId));

  return markers.filter((marker) => {
    if (marker.executionId && reachedIds.size > 0) {
      const markerId = String(marker.executionId);
      if (reachedIds.has(markerId)) return true;

      // Marker IDs may be generated by a different fallback scheme than replay
      // execution IDs. In that case, gracefully fall back to time-based gating
      // instead of hiding the marker.
    }

    const executionTimeMs = normalizeExecutionTimeToMs(marker.executionTime);
    if (executionTimeMs != null) return executionTimeMs <= replayBoundaryTimeMs;

    const markerTimeMs = normalizeMarkerTimeToMs(marker.time);
    return markerTimeMs != null && markerTimeMs <= replayBoundaryTimeMs;
  });
}

export function buildReplayStepSnapshot(params: {
  currentBarIndex: number;
  replayStartIndex: number;
  bars: ReplayBarInput[];
  executions: NormalizedReplayExecution[];
  multiplier: number;
  markPrice: number | null;
  boundaryMode?: ReplayBoundaryMode;
}): ReplayStepSnapshot {
  const boundaryMode = params.boundaryMode ?? DEFAULT_REPLAY_BOUNDARY_MODE;

  if (!Array.isArray(params.bars) || params.bars.length === 0) {
    return {
      currentBarIndex: 0,
      replayVisibleBars: 0,
      stepBarTimeMs: null,
      stepStartMs: null,
      stepEndMs: null,
      replayBoundaryTimeMs: null,
      boundaryMode,
      started: false,
      closed: false,
      statusLabel: 'WAITING',
      detailLabel: 'Awaiting first fill',
      ...buildEmptyTradeState(),
    };
  }

  const clampedBarIndex = Math.max(0, Math.min(params.bars.length - 1, Math.floor(params.currentBarIndex)));
  const stepInfo = getReplayStepBoundaryMs(clampedBarIndex, params.bars, boundaryMode);
  const reachedExecutions = getReachedExecutions(params.executions, stepInfo.replayBoundaryTimeMs);
  const tradeState = reachedExecutions.length > 0
    ? buildReplayTradeState(reachedExecutions, params.markPrice, { multiplier: params.multiplier })
    : buildEmptyTradeState();

  const isWaiting = reachedExecutions.length === 0;
  const isFullyClosed = tradeState.currentSide === 'flat' && reachedExecutions.length > 0 && reachedExecutions.length === params.executions.length;
  const statusLabel: 'WAITING' | 'LIVE' | 'CLOSED' = isWaiting ? 'WAITING' : (isFullyClosed ? 'CLOSED' : 'LIVE');
  const detailLabel = isWaiting
    ? 'Awaiting first fill'
    : tradeState.currentSide === 'flat'
      ? 'Flat - awaiting next fill'
      : `${tradeState.currentSide === 'long' ? 'Long' : 'Short'} ${Math.abs(tradeState.netQuantity)} @ ${(tradeState.avgEntryPrice ?? 0).toFixed(2)}`;

  return {
    currentBarIndex: clampedBarIndex,
    replayVisibleBars: Math.max(0, clampedBarIndex - params.replayStartIndex + 1),
    stepBarTimeMs: normalizeBarTimeToMs(params.bars[clampedBarIndex]?.time),
    stepStartMs: stepInfo.stepStartMs,
    stepEndMs: stepInfo.stepEndMs,
    replayBoundaryTimeMs: stepInfo.replayBoundaryTimeMs,
    boundaryMode,
    started: !isWaiting,
    closed: isFullyClosed,
    statusLabel,
    detailLabel,
    ...tradeState,
  };
}
